import {
  AnnotationOp,
  ANNOTATION_TYPE,
  BRUSH_SIZES,
  POINTS_THROTTLE_MS,
  encodeAnnotationMessage,
} from './annotationProtocol';

/**
 * Lightweight stroke state + undo + throttled publish for screen annotations.
 */
export default class AnnotationController {
  constructor({ send, localIdentity } = {}) {
    this._send = send || null;
    this._localIdentity = localIdentity || null;
    this.strokes = [];
    this._undoStack = [];
    this._activeRemote = new Map();
    this.tool = 'pen';
    this.color = 'red';
    this.brushSize = 'medium';
    this.enabled = true;
    this.toolbarExpanded = true;
    this.version = 0;
    this.shareTarget = null;
    this.hostOverlayBurnIn = false;
    this._activeStroke = null;
    this._pendingPoints = [];
    this._throttleTimer = null;
    this._toolbarHideTimer = null;
    this._listeners = new Set();
  }

  configure({ send, localIdentity } = {}) {
    if (send) this._send = send;
    if (localIdentity) this._localIdentity = localIdentity;
  }

  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _notify() {
    this.version += 1;
    this._listeners.forEach((fn) => {
      try {
        fn();
      } catch {
        // ignore listener errors
      }
    });
  }

  setTool(tool) {
    this.tool = tool;
    this._bumpToolbar();
    this._notify();
  }

  setColor(color) {
    this.color = color;
    this.tool = 'pen';
    this._bumpToolbar();
    this._notify();
  }

  setBrushSize(size) {
    this.brushSize = size;
    this.tool = 'pen';
    this._bumpToolbar();
    this._notify();
  }

  setEnabled(enabled) {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (enabled) {
      this.toolbarExpanded = true;
      this._scheduleToolbarHide();
    } else {
      this.toolbarExpanded = false;
      if (this._toolbarHideTimer) clearTimeout(this._toolbarHideTimer);
    }
    this._notify();
  }

  /**
   * Bind drawing to a participant's screen share. Enables drawing for every
   * viewer when identity is set so A/B/C can all mark the same active share.
   */
  setShareTarget(identity) {
    if (this.shareTarget === identity) {
      if (identity && !this.enabled) this.setEnabled(true);
      return;
    }
    this.shareTarget = identity || null;
    this.hostOverlayBurnIn = false;
    this.strokes = [];
    this._undoStack = [];
    this._activeStroke = null;
    this._activeRemote.clear();
    this._pendingPoints = [];
    if (this._throttleTimer) {
      clearTimeout(this._throttleTimer);
      this._throttleTimer = null;
    }
    if (identity) {
      this.enabled = true;
      this.toolbarExpanded = true;
      this._scheduleToolbarHide();
    }
    this._notify();
  }

  setHostOverlayBurnIn(active, { broadcast = false } = {}) {
    if (this.hostOverlayBurnIn !== active) {
      this.hostOverlayBurnIn = Boolean(active);
      this._notify();
    }
    if (broadcast) {
      this._publish({
        type: ANNOTATION_TYPE,
        op: AnnotationOp.hostOverlay,
        fromIdentity: this._localIdentity,
        shareIdentity: this.shareTarget || this._localIdentity,
        active: this.hostOverlayBurnIn,
      }, true);
    }
  }

  /** Strokes to paint in the call UI (see hostOverlayBurnIn). */
  get strokesForPaint() {
    if (this.hostOverlayBurnIn) {
      return this._activeStroke ? [this._activeStroke] : [];
    }
    const list = [...this.strokes];
    if (this._activeStroke && !list.includes(this._activeStroke)) {
      list.push(this._activeStroke);
    }
    return list;
  }

  expandToolbar() {
    this.toolbarExpanded = true;
    this._scheduleToolbarHide();
    this._notify();
  }

  bumpToolbarActivity() {
    this._bumpToolbar();
    this._notify();
  }

  _bumpToolbar() {
    this.toolbarExpanded = true;
    this._scheduleToolbarHide();
  }

  _scheduleToolbarHide() {
    if (this._toolbarHideTimer) clearTimeout(this._toolbarHideTimer);
    this._toolbarHideTimer = null;
  }

  beginStroke(point) {
    if (!this.enabled || !this.shareTarget) return;
    this._bumpToolbar();
    if (this.tool === 'eraser') {
      this._eraseAt(point);
      return;
    }
    const id = this._newStrokeId();
    const stroke = {
      id,
      color: this.color,
      width: BRUSH_SIZES[this.brushSize] || BRUSH_SIZES.medium,
      points: [point],
      fromIdentity: this._localIdentity,
    };
    this._activeStroke = stroke;
    this.strokes.push(stroke);
    this._undoStack.push(id);
    this._notify();
    this._publish({
      type: ANNOTATION_TYPE,
      op: AnnotationOp.begin,
      strokeId: id,
      fromIdentity: this._localIdentity,
      shareIdentity: this.shareTarget,
      color: stroke.color,
      width: stroke.width,
      points: [point],
    }, true);
  }

  appendPoint(point) {
    if (!this.enabled || !this.shareTarget) return;
    if (this.tool === 'eraser') {
      this._eraseAt(point);
      return;
    }
    if (!this._activeStroke) return;
    this._activeStroke.points.push(point);
    this._pendingPoints.push(point);
    // Mutate in place; skip React notify so drawing stays smooth.
    // Overlay paints via rAF from pointer handlers.
    this.version += 1;
    this._ensureThrottle();
  }

  endStroke() {
    if (!this._activeStroke) return;
    this._flushPendingPoints();
    const id = this._activeStroke.id;
    this._activeStroke = null;
    this._notify();
    this._publish({
      type: ANNOTATION_TYPE,
      op: AnnotationOp.end,
      strokeId: id,
      fromIdentity: this._localIdentity,
      shareIdentity: this.shareTarget,
    }, true);
  }

  undo() {
    if (!this._undoStack.length) return;
    const id = this._undoStack.pop();
    this.strokes = this.strokes.filter((s) => s.id !== id);
    if (this._activeStroke?.id === id) this._activeStroke = null;
    this._notify();
    this._publish({
      type: ANNOTATION_TYPE,
      op: AnnotationOp.undo,
      strokeId: id,
      fromIdentity: this._localIdentity,
      shareIdentity: this.shareTarget,
    }, true);
  }

  clearAll({ broadcast = true } = {}) {
    this.strokes = [];
    this._undoStack = [];
    this._activeStroke = null;
    this._activeRemote.clear();
    this._pendingPoints = [];
    if (this._throttleTimer) {
      clearTimeout(this._throttleTimer);
      this._throttleTimer = null;
    }
    this._notify();
    if (broadcast) {
      this._publish({
        type: ANNOTATION_TYPE,
        op: AnnotationOp.clear,
        fromIdentity: this._localIdentity,
        shareIdentity: this.shareTarget,
      }, true);
    }
  }

  applyRemote(msg) {
    if (!msg || msg.type !== ANNOTATION_TYPE) return;
    const from = msg.fromIdentity;
    if (from && this._localIdentity && from === this._localIdentity) return;

    const share = msg.shareIdentity;
    if (share && this.shareTarget && share !== this.shareTarget) return;

    if (msg.hostOverlay === true && !this.hostOverlayBurnIn) {
      this.hostOverlayBurnIn = true;
    }

    switch (msg.op) {
      case AnnotationOp.begin:
        this._remoteBegin(msg);
        break;
      case AnnotationOp.points:
        this._remotePoints(msg);
        break;
      case AnnotationOp.end:
        if (msg.strokeId) this._activeRemote.delete(msg.strokeId);
        break;
      case AnnotationOp.undo:
      case AnnotationOp.erase:
        if (!msg.strokeId) return;
        this.strokes = this.strokes.filter((s) => s.id !== msg.strokeId);
        this._activeRemote.delete(msg.strokeId);
        this._undoStack = this._undoStack.filter((id) => id !== msg.strokeId);
        this._notify();
        break;
      case AnnotationOp.clear:
        this.clearAll({ broadcast: false });
        break;
      case AnnotationOp.hostOverlay: {
        const active = msg.active === true;
        if (this.hostOverlayBurnIn !== active) {
          this.hostOverlayBurnIn = active;
          this._notify();
        }
        break;
      }
      default:
        break;
    }
  }

  _remoteBegin(msg) {
    const id = msg.strokeId;
    if (!id || this.strokes.some((s) => s.id === id)) return;
    const stroke = {
      id,
      color: msg.color || 'red',
      width: msg.width ?? BRUSH_SIZES.medium,
      points: Array.isArray(msg.points) ? [...msg.points] : [],
      fromIdentity: msg.fromIdentity,
    };
    this.strokes.push(stroke);
    this._activeRemote.set(id, stroke);
    this._notify();
  }

  _remotePoints(msg) {
    const id = msg.strokeId;
    if (!id) return;
    let stroke = this._activeRemote.get(id) || this.strokes.find((s) => s.id === id);
    if (!stroke) {
      this._remoteBegin(msg);
      return;
    }
    const pts = Array.isArray(msg.points) ? msg.points : [];
    stroke.points.push(...pts);
    this._notify();
  }

  _eraseAt(point) {
    const hitRadius = 0.03;
    let hit = null;
    for (let i = this.strokes.length - 1; i >= 0; i -= 1) {
      const stroke = this.strokes[i];
      for (const p of stroke.points) {
        const dx = p.x - point.x;
        const dy = p.y - point.y;
        if (dx * dx + dy * dy <= hitRadius * hitRadius) {
          hit = stroke;
          break;
        }
      }
      if (hit) break;
    }
    if (!hit) return;
    this.strokes = this.strokes.filter((s) => s.id !== hit.id);
    this._undoStack = this._undoStack.filter((id) => id !== hit.id);
    this._notify();
    this._publish({
      type: ANNOTATION_TYPE,
      op: AnnotationOp.erase,
      strokeId: hit.id,
      fromIdentity: this._localIdentity,
      shareIdentity: this.shareTarget,
    }, true);
  }

  _ensureThrottle() {
    if (this._throttleTimer) return;
    this._throttleTimer = setTimeout(() => {
      this._throttleTimer = null;
      this._flushPendingPoints();
    }, POINTS_THROTTLE_MS);
  }

  _flushPendingPoints() {
    if (!this._pendingPoints.length || !this._activeStroke) {
      this._pendingPoints = [];
      return;
    }
    const batch = this._pendingPoints.splice(0);
    this._publish({
      type: ANNOTATION_TYPE,
      op: AnnotationOp.points,
      strokeId: this._activeStroke.id,
      fromIdentity: this._localIdentity,
      shareIdentity: this.shareTarget,
      color: this._activeStroke.color,
      width: this._activeStroke.width,
      points: batch,
    }, false);
  }

  _publish(payload, reliable) {
    if (!this._send) return;
    try {
      const body = this.hostOverlayBurnIn
        ? { ...payload, hostOverlay: true }
        : payload;
      this._send(encodeAnnotationMessage(body), reliable);
    } catch {
      // drop
    }
  }

  _newStrokeId() {
    return `${this._localIdentity || 'local'}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  }

  dispose() {
    if (this._throttleTimer) clearTimeout(this._throttleTimer);
    if (this._toolbarHideTimer) clearTimeout(this._toolbarHideTimer);
    this._listeners.clear();
  }
}
